import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import useCompleteBookingPayment, {
  BookingPaymentErrorDetails,
  getBookingPaymentErrorDetails,
  isUnconfirmedOutcomeError,
} from "../../hooks/useCompleteBookingPayment";
import { useTrips } from "../../context/TripsContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Airplane,
  CaretLeft,
  Check,
  ClockCountdown,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { HashLoader } from "react-spinners";
import { Field, Form, Formik } from "formik";
import * as yup from "yup";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import BookingRecoveryNotice from "../../components/BookingRecoveryNotice";
import { APIError } from "../../api";
import { formatAsMoney, parseMoney } from "../../helpers/numbers";

const SCHEMA = yup.object().shape({
  accepted: yup.bool().isTrue("You have to accept the terms and conditions"),
});

const WALLET_OPTION_TAG = "pay_now_wallet";
/**
 * The option code arrives as "003" from some responses and as the number 3 from
 * others (PHP octal literals in the backend, JSON numbers on the way out), so it
 * is compared numerically rather than by string.
 */
const WALLET_OPTION_CODE = 3;

/** Funding started here returns to payment options with the wallet preselected. */
const FUND_FROM_CHECKOUT_STATE = {
  returnTo: "/aviation/select-payment-method",
  returnState: { preferWallet: true },
};

const NON_RETRY_TITLES: Record<BookingPaymentErrorDetails["error_code"], string> =
  {
    WALLET_BOOKING_PENDING: "Checking your booking",
    BOOKING_ALREADY_PAID: "This booking is already paid",
    BOOKING_ALREADY_TICKETED: "This booking is already ticketed",
  };

/**
 * Shown for outcomes that must not be paid for a second time: the money either
 * already left the wallet or we never learnt whether it did. Same shell as the
 * other overlays on this page, minus any retry affordance.
 */
const DoNotRetryOverlay = ({
  icon,
  title,
  message,
  reference,
  onViewFlights,
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
  reference?: string;
  onViewFlights: () => void;
}) => (
  <motion.div
    initial={{ y: "100%" }}
    animate={{ y: 0 }}
    exit={{ y: "100%" }}
    transition={{
      type: "tween",
      ease: "anticipate",
    }}
    role="alert"
    aria-live="assertive"
    className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full overflow-y-auto"
  >
    <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
      <div className="bg-amber-500 rounded-full p-8 text-white">{icon}</div>
      <div className="flex flex-col items-center gap-2 w-full">
        <div className="text-3xl font-medium">{title}</div>
        {/* Never clamped: this text is the whole explanation of what happened to
            the customer's money, and a cut-off sentence is what makes someone
            pay twice. */}
        <div className="w-full">{message}</div>
        <div className="font-medium">Please do not pay again.</div>
        {reference && (
          <div className="text-gray-500 text-sm">
            Reference: <span className="font-medium">{reference}</span>
          </div>
        )}
      </div>
      <div className="flex flex-col items-center gap-4">
        <button
          onClick={onViewFlights}
          type="button"
          className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium"
        >
          <span>View Booked Flights</span>
        </button>
        <Link to="/support" className="text-visaro-orange font-medium underline">
          Contact support
        </Link>
      </div>
    </div>
  </motion.div>
);

const PayNow = () => {
  const { flightData, setBooking } = useTrips();

  const { state: params } = useLocation();

  const { user, refetch } = useAuth();

  const navigate = useNavigate();

  const [confirming, setConfirming] = useState(false);
  const [bookingError, setBookingError] = useState(false);
  const [booked, setBooked] = useState(false);
  const [nonRetryDetails, setNonRetryDetails] =
    useState<BookingPaymentErrorDetails | null>(null);
  const [outcomeUnconfirmed, setOutcomeUnconfirmed] = useState(false);
  const [insufficientFunds, setInsufficientFunds] = useState(false);

  // Survives re-renders without one, so a double click can't fire two charges.
  const submittingRef = useRef(false);

  const optionCode = params?.paymentData?.option?.code;

  const isWalletPayment =
    params?.paymentData?.option_data?.tag === WALLET_OPTION_TAG ||
    Number(optionCode) === WALLET_OPTION_CODE;

  // The total, in kobo, this screen is showing the customer. The backend refuses
  // to debit unless it matches the total it computes, so a fare that moved while
  // this page sat open is caught instead of silently charged at the new price.
  const expectedTotalKobo =
    params?.paymentData?.payment_data?.total_kobo ??
    params?.paymentData?.payment_data?.total_amount_payable;

  // A refresh drops the booking (context) and the payment quote (route state).
  // Never render a confirm flow with an undefined booking or a blank amount.
  const bookingId = flightData?.id;
  const quote = params?.paymentData?.payment_data;
  const payable = Number(quote?.total_amount_payable ?? quote?.amount);
  const canPay =
    !!bookingId &&
    !!quote &&
    !!optionCode &&
    Number.isFinite(payable) &&
    payable > 0;

  // Wallet only: what the customer has against what this quote debits. The
  // backend re-checks under a lock; this just says so before they tap Pay.
  const walletBalance = parseMoney(user?.wallet?.available_balance);
  const totalNaira = Number(quote?.total_naira ?? payable / 100);
  const shortfall =
    isWalletPayment && user?.wallet && Number.isFinite(totalNaira)
      ? Math.max(0, Math.ceil(totalNaira - walletBalance))
      : 0;
  const fundWallet = () =>
    navigate("/fund", { state: FUND_FROM_CHECKOUT_STATE });

  const {
    confirmBookingPayment,
    isConfirmingPaymentError,
    confirmingBookingPayment,
    confirmBookingPaymentError,
  } = useCompleteBookingPayment({
    onMutate(variables) {
      console.log("Variables-", variables);
    },
    onError(error) {
      console.log(error);

      // 409s carry a settled/duplicate booking: surface the details, never a retry.
      const details = getBookingPaymentErrorDetails(error);
      if (details) {
        setNonRetryDetails(details);
        return;
      }

      // A wallet charge that lost the response may still have debited the user.
      if (isWalletPayment && isUnconfirmedOutcomeError(error)) {
        setOutcomeUnconfirmed(true);
        return;
      }

      const code =
        error instanceof APIError
          ? (error.responseData?.data as { error_code?: string } | undefined)
              ?.error_code
          : undefined;
      setInsufficientFunds(code === "WALLET_INSUFFICIENT_FUNDS");
      setBookingError(true);
    },
    onSuccess(data) {
      setBooked(true);
      setBooking(false);
      toast.success(data.message);
    },
    onSettled() {
      submittingRef.current = false;
      // The wallet balance can move even when the request errors, so refresh the
      // profile on every outcome rather than on success only.
      refetch();
    },
  });

  // Anything that leaves the charge settled or unknown must block another
  // attempt — including a payment that already succeeded on this screen, which
  // is the most expensive thing to repeat.
  const retryBlocked = booked || outcomeUnconfirmed || nonRetryDetails !== null;

  const handleConfirmPayment = () => {
    if (
      !bookingId ||
      submittingRef.current ||
      confirmingBookingPayment ||
      retryBlocked
    ) {
      return;
    }

    submittingRef.current = true;
    setConfirming(false);
    confirmBookingPayment({
      booking_id: bookingId,
      payment_option_id: optionCode,
      payment_installment_id:
        params?.paymentData?.payment_data?.payment_installment_id,
      // Only the wallet option quotes and debits a single total, so only it has
      // a figure to hold the backend to. The other options are unchanged.
      ...(isWalletPayment && expectedTotalKobo != null
        ? { expected_total_kobo: Number(expectedTotalKobo) }
        : {}),
    });
  };

  if (!canPay) {
    return (
      <div className="flex justify-center px-5 py-20">
        <div className="w-full max-w-xl">
          <BookingRecoveryNotice showPaymentOptions={!!bookingId} />
        </div>
      </div>
    );
  }

  return (
    <div>
      {confirmingBookingPayment && (
        <div className="fixed top-0 z-[99] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      {confirming && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "tween",
            ease: "anticipate",
          }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-visaro-primary-700 rounded-full p-8 text-white">
              <Airplane size={36} />
            </div>
            <div className="text-center flex flex-col gap-2">
              <div className="text-3xl font-medium">Are you sure?</div>
              <div>
                You are about to be charged ₦{" "}
                {
                  params?.paymentData?.payment_data?.formatted
                    ?.total_amount_payable
                }{" "}
                for this booking.
              </div>
            </div>
            <div className="flex justify-between w-full">
              <button
                onClick={() => setConfirming(false)}
                type="button"
                className="p-2 py-3 text-center bg-red-100 text-red-500 px-6 ring-2 ring-red-100/50 rounded-xl font-medium flex gap-2 items-center"
              >
                <span>Cancel</span>
              </button>
              <button
                onClick={handleConfirmPayment}
                type="button"
                disabled={
                  !bookingId || confirmingBookingPayment || retryBlocked
                }
                className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Pay
              </button>
            </div>
          </div>
        </motion.div>
      )}
      {isConfirmingPaymentError && bookingError && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "tween",
            ease: "anticipate",
          }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-red-500 rounded-full p-8 text-white">
              <X size={36} />
            </div>
            <div className="flex flex-col items-center gap-2 w-full">
              <div className="text-3xl font-medium">Booking Failed!</div>
              <div className="w-full line-clamp-6">
                {confirmBookingPaymentError?.message.toString()}
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setBookingError(false)}
                type="button"
                className="p-2 py-3 text-center bg-red-100 text-red-500 px-6 ring-2 ring-red-100/50 rounded-xl font-medium flex gap-2 items-center"
              >
                <span>Cancel</span>
              </button>
              {insufficientFunds && (
                <button
                  onClick={fundWallet}
                  type="button"
                  className="p-2 py-3 text-center bg-deepBlue text-white px-6 ring-2 ring-deepBlue/50 rounded-xl font-medium"
                >
                  Fund wallet
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
      {nonRetryDetails && (
        <DoNotRetryOverlay
          icon={<ClockCountdown size={36} />}
          title={NON_RETRY_TITLES[nonRetryDetails.error_code]}
          message={
            confirmBookingPaymentError?.message.toString() ||
            "We are confirming this booking with the airline."
          }
          reference={nonRetryDetails.reference}
          onViewFlights={() => navigate("/aviation/your-flights")}
        />
      )}
      {outcomeUnconfirmed && (
        <DoNotRetryOverlay
          icon={<WarningCircle size={36} />}
          title="Payment outcome unconfirmed"
          message="We didn't get a clear answer back for this payment, so we can't yet tell whether your wallet was charged. This can happen if the connection dropped or if something went wrong on our side after the request reached us. Check your booked flights in a few minutes, or contact support with your booking details."
          onViewFlights={() => navigate("/aviation/your-flights")}
        />
      )}
      {booked && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "tween",
            ease: "anticipate",
          }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-green-500 rounded-full p-8 text-white">
              <Check size={36} />
            </div>
            <div className="text-center flex flex-col gap-2">
              <div className="text-3xl font-medium">Booking confirmed!</div>
              <div>
                {/* The wallet path does not send a confirmation email, so it must
                    not promise one. The other options are unchanged. */}
                {isWalletPayment
                  ? "Congratulations! Your booking order has been processed. You can see your ticket under your booked flights. Enjoy your trip!"
                  : "Congratulations! Your booking order has been processed. An email has been sent to you as a confirmation of this flight booking. Enjoy your trip!"}
              </div>
            </div>
            <button
              onClick={() => {
                navigate("/aviation/your-flights");
                setBooked(false);
              }}
              type="button"
              className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium"
            >
              <span>View Booked Flights</span>
            </button>
          </div>
        </motion.div>
      )}
      <Formik
        validationSchema={SCHEMA}
        initialValues={{
          accepted: false,
        }}
        onSubmit={() => {
          if (submittingRef.current || confirmingBookingPayment || retryBlocked) {
            return;
          }
          setConfirming(true);
        }}
      >
        {({ isValid, dirty }) => {
          return (
            <Form className="relative w-full flex items-center justify-center gap-10 px-5 py-20 flex-col">
              <div className="flex flex-col gap-5 max-w-4xl w-full items-start">
                <div className="font-semibold text-3xl">Pay Now</div>
                <div className="text-gray-500">You'll be charged</div>
                <div className="text-3xl font-semibold">
                  ₦{" "}
                  {
                    params?.paymentData?.payment_data?.formatted
                      ?.total_amount_payable
                  }
                </div>
                <div>{params?.paymentData?.option?.description}</div>
                {isWalletPayment && user?.wallet && (
                  <div
                    className={`w-full rounded-xl border p-4 flex flex-col gap-3 ${
                      shortfall > 0
                        ? "border-amber-200 bg-amber-50"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-600">Wallet balance</span>
                      <span className="font-semibold">
                        ₦{formatAsMoney(walletBalance.toFixed(2))}
                      </span>
                    </div>
                    {shortfall > 0 && (
                      <>
                        <div className="text-sm text-amber-900">
                          You need ₦{formatAsMoney(shortfall)} more to pay for
                          this flight. Fund your wallet with your card or by
                          bank transfer, then come back to pay.
                        </div>
                        <div className="flex flex-wrap gap-3">
                          <button
                            type="button"
                            onClick={fundWallet}
                            className="p-2 px-5 rounded-lg font-medium bg-deepBlue text-white text-sm"
                          >
                            Fund wallet
                          </button>
                          <button
                            type="button"
                            onClick={() => refetch()}
                            className="p-2 px-5 rounded-lg font-medium border border-gray-300 text-deepBlue text-sm bg-white"
                          >
                            Refresh balance
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <br />
                <div
                  style={{
                    flexDirection: "row",
                  }}
                >
                  <div
                    style={{
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    <div className="font-medium">Transaction Cost</div>
                    <div className="text-lg">
                      ₦
                      {
                        params?.paymentData?.payment_data?.formatted
                          ?.processing_fee
                      }{" "}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Field
                    type="checkbox"
                    name="accepted"
                    className="w-4 aspect-square accent-deepBlue"
                  />{" "}
                  <span>
                    By clicking ‘Continue’ , you agree to{" "}
                    <Link
                      to=""
                      className="text-visaro-orange font-medium underline"
                    >
                      Terms and Condition
                    </Link>{" "}
                    &{" "}
                    <Link
                      to=""
                      className="text-visaro-orange font-medium underline"
                    >
                      Privacy Policy
                    </Link>
                  </span>
                </div>
              </div>
              <div className="flex justify-between w-full max-w-4xl">
                <button
                  onClick={() => navigate(-1)}
                  type="button"
                  className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
                >
                  <CaretLeft />
                  <span>Back</span>
                </button>
                <button
                  disabled={
                    !isValid ||
                    !dirty ||
                    confirmingBookingPayment ||
                    retryBlocked ||
                    shortfall > 0
                  }
                  type="submit"
                  className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Pay
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default PayNow;
